/*
 * TODO: Rethink the entirety of our app initialization. It starts with connecting our reducers
 * and leads here to dispatching settings into the store and doing intl stuff. We probably don't
 * need and shouldn't get settings here.
 */
import {datadogLogs} from '@datadog/browser-logs';
import {datadogRum} from '@datadog/browser-rum';
import * as hist from 'history';
import React from 'react';
import ReactDOM from 'react-dom';
import {Provider} from 'react-redux';
import {Route, Switch} from 'react-router-dom';

require('webrtc-adapter'); // normalizes some WebRTC implementation differences between browsers

import isNil from 'lodash/isNil';

import {getLaunchDarklyProviderConfig, setupLDClient} from 'ld-client';
import AppStore from 'stores/app-store';
import {createConnectedRouter} from 'stores/connected-react-router/connected-router';
import IntlStore from 'stores/intl-store';
import SessionStore from 'stores/session-store';
import AppRoutes from './app-routes';

import {getSettings} from 'actions/session-actions';
import {LDProvider} from 'launchdarkly-react-client-sdk';
import {connectReducer, initializeReduxStore} from 'redux-store';

// Constants
import * as EnvironmentConstants from './constants/environment-constants';

// Utils
import {configureCanvasJS} from 'utils/canvasjs-utils';
import IntlUtils from 'utils/intl-utils';
import Logger from 'utils/logger';

const logger = new Logger('main');
const isDatadogEnabled = __IS_DATADOG_ENABLED__;
const deployment = __DEPLOYMENT__;
const environment = deployment.includes('production') ? 'production' : deployment;

const browserHistory = hist.createBrowserHistory();

const ConnectedRouter = createConnectedRouter();

if (isNil(environment)) {
    throw new Error(`Unable to initialize DataDog or LaunchDarkly with environment: ${environment}`);
}

//#region DataDog
// Confluence: https://blinemedical.atlassian.net/wiki/spaces/SD/pages/3164078092
try {
    if (isDatadogEnabled) {
        let ddApplicationId;
        let ddClientToken;
        if (environment === 'production') {
            ddApplicationId = EnvironmentConstants.DATADOG_PRODUCTION_SIMCAPTURE_CLOUD_APPLICATION_ID;
            ddClientToken = EnvironmentConstants.DATADOG_PRODUCTION_SIMCAPTURE_CLOUD_CLIENT_TOKEN;
        } else {
            // All non-production deployments use the development application.
            ddApplicationId = EnvironmentConstants.DATADOG_DEVELOPMENT_SIMCAPTURE_CLOUD_APPLICATION_ID;
            ddClientToken = EnvironmentConstants.DATADOG_DEVELOMENT_SIMCAPTURE_CLOUD_CLIENT_TOKEN;
        }

        const datadogOptions = {
            clientToken: ddClientToken,
            env: environment,
            site: 'datadoghq.com',
            service: EnvironmentConstants.SERVICE_CONSUMERS.SIMCAPTURE_CLOUD,
            version: __VERSION__,
            sampleRate: 100,
        };

        datadogLogs.init({
            ...datadogOptions,
            forwardConsoleLogs: 'all',
        });

        datadogRum.init({
            ...datadogOptions,
            applicationId: ddApplicationId,
            trackInteractions: true,
            silentMultipleInit: true,
            allowedTracingUrls: [
                // Add datadog tracing headers to requests to simcapture API
                // so that we can tie rum sessions to apm traces
                /https:\/\/.*\.simcapture-dev\.com.*/,
                /https:\/\/api.*\.simcapture-staging\.com.*/,
                /https:\/\/api.*\.simcapture\.com.*/,
            ],
            beforeSend: (event) => {
                event.context = {
                    ...event.context,
                    deployment,
                    ui_service: __UI_SERVICE__,
                };
            },
        });
        // Enable Session Replay
        datadogRum.startSessionReplayRecording();
    }
} catch (err) {
    logger.error(`Failed to initialize RUM monitoring for environment: ${environment}`, err);
}
//#endregion DataDog

// This logs the UI version to the console and is very useful for troubleshooting. Please do not delete it
// eslint-disable-next-line
console.log(__VERSION__);

const launchDarklyProviderConfig = getLaunchDarklyProviderConfig(environment);

const renderTheApplication = async (store) => {
    const launchDarklyClient = await setupLDClient(launchDarklyProviderConfig);

    ReactDOM.render(
        <div>
            <LDProvider {...launchDarklyProviderConfig} ldClient={launchDarklyClient}>
                <Provider store={store} key='provider'>
                    <ConnectedRouter history={browserHistory}>
                        <Switch>
                            <Route path='/'>
                                <AppRoutes />
                            </Route>
                        </Switch>
                    </ConnectedRouter>
                </Provider>
            </LDProvider>
        </div>,
        document.getElementById('root'),
    );
};

const handleFailure = async (store, error) => {
    // log error and render anyway
    console.error('handleFailure', error);
    await renderTheApplication(store);
};

// todo: polyfill intl cldr data if needed
const setupIntl = async (store) => {
    const settings = store.getState().session.settings;

    IntlUtils.updateTimeFormats(settings);
    await renderTheApplication(store);
};

try {
    connectReducer('app', AppStore);
    connectReducer('intl', IntlStore);
    connectReducer('session', SessionStore);

    const store = initializeReduxStore(browserHistory);
    configureCanvasJS();

    // Get application global settings ie: locale etc..
    store.dispatch(
        // @ts-expect-error TS2349: This expression is not callable.
        getSettings(setupIntl.bind(this, store), handleFailure.bind(this, store)),
    );
} catch (err) {
    console.error(err);
    ReactDOM.render(
        <div>
            <h2> Error: Application could not load. </h2>
            <pre>
                <strong>{err.toString()}</strong>
                {!!err.stack && (
                    <div>
                        <br /> {err.stack}{' '}
                    </div>
                )}
            </pre>
        </div>,
        document.getElementById('root'),
    );

    throw err;
}
